import React, { ChangeEvent, FocusEvent, useState } from "react";
import { CgChevronDown } from "react-icons/cg";
import { Cell } from "src/modules/fithub-shared/data";
import { Background, ContentHeader, Select } from "./styles";

import {
  ExerciseFields,
  ExerciseValue,
  FieldType,
} from "src/modules/fithub-shared/interfaces/exercise";

interface Props {
  mnax: ExerciseFields;
  maxNums?: number;
  dragging: boolean;
  cell: Cell;
  inputProps: React.HTMLProps<HTMLInputElement>;
  series?: number;
  defaultValues: ExerciseValue;
  onChange?: (value: ExerciseValue) => void;
  enableSelectTab: boolean;
}

const DragInput: React.FC<Props> = ({
  dragging,
  inputProps,
  cell,
  maxNums = 3,
  series,
  onChange,
  defaultValues,
  enableSelectTab,
}) => {
  const [option, setOption] = useState(defaultValues.option);
  const placeholder = inputProps.placeholder;

  const clampedSeries = (value = 0): number => Math.max(1, Math.min(value, 9));

  const getDefault = (index: number): number => {
    const value = defaultValues.value[index];
    return value ?? 0;
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    e.target.value = value;
  };

  const handleInputBlur = (e: FocusEvent<HTMLInputElement>): void => {
    const index = e.target.getAttribute("data-index");
    if (!index || !onChange) return;

    const numericValue = parseInt(e.target.value);
    const indexNum = parseInt(index);

    if (defaultValues.value[indexNum] !== numericValue) {
      const values = { ...defaultValues };
      values.value[indexNum] = numericValue;
      onChange(values);
    }
  };

  const handleOptionChange = (newOption: number): void => {
    if (onChange) {
      onChange({ ...defaultValues, option: newOption });
    }
    setOption(newOption);
  };

  const renderInput = (index: number) => (
    <input
      className="flex"
      key={index}
      data-index={index}
      autoComplete="off"
      autoCorrect="off"
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      maxLength={maxNums}
      defaultValue={getDefault(index)}
      onFocus={(e) => e.target.select()}
      onBlur={handleInputBlur}
      onChange={handleInputChange}
      {...inputProps}
    />
  );

  const renderDescription = (description: string) => {
    const truncatedDesc = description.substring(0, 45);
    return (
      <p
        className="h-fit w-full self-center text-center"
        title={description}
        style={{
          fontSize: 9,
          fontWeight: "bold",
          fontStyle: "italic",
          color: "#000",
        }}
      >
        {`${truncatedDesc}${truncatedDesc.length >= 45 ? "..." : ""}`}
      </p>
    );
  };

  const renderInputs = () => {
    const currentData = cell.data[option];

    switch (currentData.field_type) {
      case FieldType.NUMBER_4:
        return Array(4)
          .fill(0)
          .map((_, i) => renderInput(i));
      case FieldType.NUMBER_2:
        return Array(2)
          .fill(0)
          .map((_, i) => renderInput(i));
      case FieldType.DESCRIPTION:
        return renderDescription(currentData.description);
      case FieldType.PYRAMID:
        return Array(clampedSeries(series))
          .fill(0)
          .map((_, i) => renderInput(i));
      default:
        return renderInput(0);
    }
  };

  return (
    <div className="flex flex-col">
      <ContentHeader className="row flex">
        <h1>{cell.data[option].title ?? placeholder}</h1>
        <CgChevronDown size={12} />
        <Select
          tabIndex={enableSelectTab ? 1 : -1}
          value={option}
          onChange={(e) => handleOptionChange(e.target.selectedIndex)}
          onFocus={(e) => {
            if (enableSelectTab) {
              e.target.dispatchEvent(new MouseEvent("mousedown"));
            }
          }}
        >
          {cell.data.map((element, index) => (
            <option key={index} value={index} title={element.description}>
              {element.title}
            </option>
          ))}
        </Select>
      </ContentHeader>
      <Background>
        {dragging ? <h3>{defaultValues.value[0]}</h3> : renderInputs()}
      </Background>
    </div>
  );
};

export default DragInput;
